import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AuthorizeNetGetCustomerProfileResponse } from "../../models/payment-gateway-responses.interface";

/**
 * Authorize.Net payment gateway service
 * Handles API calls to Authorize.Net for customer profile retrieval
 */
@Injectable({
  providedIn: "root",
})
export class AuthorizeNetService {
  private productionUrl = "https://api.authorize.net/xml/v1/request.api";
  private sandboxUrl = "https://apitest.authorize.net/xml/v1/request.api";

  constructor(private http: HttpClient) {}

  /**
   * Get customer profile information from Authorize.Net
   * Matches old QMC authorize-net.service.ts:20-26
   * @param customerProfileId Authorize.Net customer profile ID
   * @param apiLogin API login ID
   * @param transactionKey Transaction key
   * @param isSandbox Whether to use sandbox environment
   * @returns Observable of Authorize.Net customer profile response
   */
  getCustomerInformation(
    customerProfileId: string,
    apiLogin: string,
    transactionKey: string,
    isSandbox: boolean,
  ): Observable<AuthorizeNetGetCustomerProfileResponse> {
    const endpoint = isSandbox ? this.sandboxUrl : this.productionUrl;

    // Build Authorize.Net API request (matches old QMC line 22)
    const request = {
      getCustomerProfileRequest: {
        merchantAuthentication: {
          name: apiLogin,
          transactionKey: transactionKey,
        },
        customerProfileId: customerProfileId,
        unmaskExpirationDate: "true",
        includeIssuerInfo: "true",
      },
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http
      .post<AuthorizeNetGetCustomerProfileResponse>(endpoint, request, {
        headers,
      })
      .pipe(
        map((response) => {
          // Validate response
          if (
            response.messages.resultCode === "Error" &&
            response.messages.message
          ) {
            const errorMessage =
              response.messages.message.map((m) => m.text).join(", ") ||
              "Unknown error from Authorize.Net";
            throw new Error(errorMessage);
          }
          return response;
        }),
      );
  }
}
